import React, { useRef, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Popup from "../components/popup.js";
import recright from "../assets/Home/recright.webp";
// Import images from Managed folder (reusing existing images)
import useCase1Img from "../assets/Home/Usecase 1.jpeg";
import useCase2Img from "../assets/Home/Usecase 2.png";
import useCase3Img from "../assets/Home/Usecase 3.jpg";
import useCase4Img from "../assets/Home/Use case 4.png";
import useCase5Img from "../assets/Home/Usecase 5.png";
import bubble1 from "../assets/Managed/bubble1.webp";
import line from "../assets/Managed/line.webp";
import triangle from "../assets/Managed/triangle.webp";

const UseCases = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    // Set page title
    document.title = "Random Walk | Real-World AI Use Cases";

    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Discover how organizations across industries are leveraging our AI platforms to solve complex challenges and achieve remarkable results. Real transformational stories of AI in action."
      );
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content =
        "Discover how organizations across industries are leveraging our AI platforms to solve complex challenges and achieve remarkable results. Real transformational stories of AI in action.";
      document.head.appendChild(meta);
    }

    // Add or update Open Graph title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement("meta");
      ogTitle.setAttribute("property", "og:title");
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute("content", "Random Walk | Real-World AI Use Cases");

    // Add or update Open Graph description
    let ogDescription = document.querySelector(
      'meta[property="og:description"]'
    );
    if (!ogDescription) {
      ogDescription = document.createElement("meta");
      ogDescription.setAttribute("property", "og:description");
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute(
      "content",
      "Discover how organizations across industries are leveraging our AI platforms to solve complex challenges and achieve remarkable results."
    );

    // Add viewport meta tag
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement("meta");
      viewport.name = "viewport";
      document.head.appendChild(viewport);
    }
    viewport.setAttribute("content", "width=device-width, initial-scale=1");

    // Add theme-color meta tag
    let themeColor = document.querySelector('meta[name="theme-color"]');
    if (!themeColor) {
      themeColor = document.createElement("meta");
      themeColor.name = "theme-color";
      document.head.appendChild(themeColor);
    }
    themeColor.setAttribute("content", "#000000");

    // Add Open Graph image
    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement("meta");
      ogImage.setAttribute("property", "og:image");
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute("content", `${process.env.PUBLIC_URL}/rw_logo.png`);

    // Add Open Graph type
    let ogType = document.querySelector('meta[property="og:type"]');
    if (!ogType) {
      ogType = document.createElement("meta");
      ogType.setAttribute("property", "og:type");
      document.head.appendChild(ogType);
    }
    ogType.setAttribute("content", "website");

    // Add canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = "https://randomwalk.ai/use-cases/";
  }, []);

  const useCases = [
    {
      id: 1,
      title: "AI-Powered Scouting Intelligence for Professional Football",
      subtitle: "Textual AI + Analytical AI + Functional AI",
      para: "A Premier League club sought to enhance the efficiency and intelligence of its global scouting operations. With thousands of reports being generated by scouts worldwide, the club needed a way to bring structure, consistency, and deeper insights into its player evaluation process.",
      industry: "Government",
      image: useCase1Img,
      challenge:
        "Scouting reports varied widely in quality, depth, and structure. Data from multiple scouts lacked consistency, making it difficult to compare player potential, identify replacements, or track historical patterns. Manual review processes also limited scalability and led to subjective decision-making.",
      solution:
        "A custom AI-driven scouting intelligence platform was developed to analyze, structure, and interpret unstructured scout reports.Textual AI standardized and categorized reports, performing advanced summarization and attribute tagging. Analytical AI generated insight scores, report-depth metrics, and player success prediction models. Functional AI powered semantic search capabilities, enabling scouts and analysts to find similar players, evaluate report quality, and identify potential replacements through contextual, data-backed recommendations.",
      results: [
        "The platform transformed qualitative scouting data into quantifiable intelligence. It streamlined talent assessment, enhanced decision-making accuracy, and enabled data-driven recruitment strategies with faster, more reliable insights across global scouting networks.",
      ],
      readMoreLink: "#",
      demoLink: "Request Demo",
    },
    {
      id: 2,
      title:
        "AI-Driven Stadium Intelligence for Sports and Security Operations",
      subtitle: "Visual AI + Textual AI + Analytical AI",
      industry: "Retail",
      para: "During the IPL 2025 season, a large-scale AI deployment was implemented to enhance both fan safety and team insight. The solution combined real-time visual monitoring and sentiment analytics to support on-ground security teams and franchise management.",
      image: useCase2Img,
      challenge:
        "Managing massive stadium crowds in real time posed multiple challenges - monitoring crowd density, identifying potential threats, and detecting disruptive activity across thousands of spectators. Simultaneously, the team wanted a live pulse of fan sentiment around players and matches to guide engagement strategies.",
      solution:
        "Visual AI processed live CCTV feeds to quantify foot traffic, differentiate fan segments by jersey color, detect thefts or conflicts, and flag inappropriate content such as profane placards or aggressive behavior. Analytical dashboards delivered these insights instantly to the police control room for rapid response.In parallel, Textual AI analyzed social media data - comments, mentions, and reactions - using sentiment models to assess fan perception of individual players and overall brand engagement.",
      results: [
        "The combined solution enabled proactive security response, reduced incident turnaround times, and provided the franchise with actionable sentiment intelligence. It demonstrated how Visual and Textual AI can work in tandem to enhance safety, operational efficiency, and fan engagement during high-stakes live events.",
        ,
      ],
      readMoreLink: "#",
      demoLink: "Request Demo",
    },
    {
      id: 3,
      title: "Visual AI Surveillance for Mega Public Events",
      subtitle: "Visual AI + Functional AI",
      industry: "Corporate Training",
      para: "During Ganesh Chaturthi in Mumbai - one of India’s largest annual public gatherings - an AI-powered surveillance system was deployed to support citywide safety and crowd management operations over a ten-day period",
      image: useCase3Img,
      challenge:
        "Managing millions of devotees across multiple high-density locations such as Lalbaugcha Raja and Visarjan routes posed an immense challenge for the Mumbai Police. Traditional surveillance setups could not efficiently monitor crowd density, detect unrest, or guide real-time deployment decisions. Heightened political sensitivities and the scale of the event demanded an intelligent, always-on monitoring solution.",
      solution:
        "Visual AI systems analyzed live CCTV and drone feeds to track crowd flow, measure footfall intensity, and identify potential hotspots across various checkpoints. The AI models detected unusual behavior, conflicts, or politically affiliated groups to pre-empt disturbances. A unified dashboard provided command-center visibility for senior officials - including the police commissioner and ministers - showing real-time alerts, density heatmaps, and drone analytics. Functional AI modules enabled predictive crowd movement analysis and guided smart deployment of police personnel based on evolving conditions.",
      results: [
        "The platform empowered law enforcement with a real-time, AI-driven command-and-control capability. Over the 10-day festival, the system supported safe crowd movement exceeding four million people, prevented potential conflicts, and enabled precise, data-backed deployment of security forces - ensuring a smooth and incident-free celebration.",
      ],
      readMoreLink: "#",
      demoLink: "Request Demo",
    },
    {
      id: 4,
      title: "Conversational Decision Intelligence for Financial Services",
      subtitle: "Conversational AI + Analytical AI + Textual AI",
      industry: "Law Enforcement",
      image: useCase4Img,
      para: "A leading fintech enterprise aimed to enhance how financial providers assess loan applications by enabling chat-based decision intelligence. The company’s platform aggregates vast financial data - including income, payment, debt, identity, and business information - to analyze the causes behind pending or declined applications.",
      challenge:
        "Credit and operations teams found it challenging to manually explore complex datasets to identify loan eligibility patterns or understand rejections. Traditional analytics dashboards required technical know-how and were time-consuming for finance professionals who needed quick, actionable insights.",
      solution:
        "A chat-based conversational AI was developed to help financial providers interact directly with the data. Through a simple conversational interface, users can ask questions such as “Why was this application declined?”, “Show me similar cases with successful approval”, or “List top reasons for rejections last quarter.”Behind the chat, Analytical AI processes financial and behavioral data to detect risk trends and anomalies, while Textual AI converts those insights into natural-language responses.",
      results: [
        "This solution transformed static analytics into an interactive decision-support tool, allowing faster and more transparent evaluations. Financial providers can now understand, explain, and act on credit decisions with greater accuracy and speed - improving both operational efficiency and customer trust.",
      ],
      readMoreLink: "#",
      demoLink: "Request Demo",
    },
    {
      id: 5,
      title: "Enterprise Knowledge Continuum",
      subtitle:
        "Knowledge Nudge + Chateleon + AI Readiness Assessment + Fortune Cookie",
      industry: "Political Campaigns",
      image: useCase5Img,
      para: "A global consulting enterprise sought to create an integrated intelligence layer across its operations - connecting learning, leadership insights, workforce readiness, and organizational strategy. The Business Enterprise Suite became the central engine driving continuous intelligence and alignment across departments.",
      challenge:
        "A political party needed a way to understand public sentiment across diverse voter demographics. Traditional polling methods were slow, expensive, and often inaccurate in capturing real-time voter concerns.",
      solution:
        "The enterprise conducted an AI Readiness Assessment to identify gaps in digital capability, operational efficiency, and innovation maturity. Knowledge Nudge transformed training material into ongoing, adaptive learning cycles that reinforced critical skills. Chateleon served as an intelligent conversational interface connecting employees to knowledge, insights, and support instantly. Fortune Cookie empowered the leadership team with AI-driven summaries, visual insights, and predictive recommendations drawn from enterprise-wide data, enabling informed and timely decisions.",
      results: [
        "The Business Enterprise Suite established a connected ecosystem where insights, learning, and strategy informed one another. Leaders gained visibility into organizational readiness, employees accessed knowledge on demand, and teams operated with data-backed confidence - creating a culture of continuous intelligence and informed action across the enterprise.",
      ],
      readMoreLink: "#",
      demoLink: "Request Demo",
    },
  ];

  const handleOpenPopup = () => {
    setIsPopupOpen(true);
  };

  return (
    <section className="Main2 font-display mt-16 md:mt-24">
      <div className="text-center mb-12">
        <h1
          className="font-bold text-[#3E59B7] mb-4"
          style={{ fontSize: "40px" }} // lg:text-[40px]
        >
          Intelligence in Action
        </h1>
        <p
          className="text-[#666666] max-w-4xl mx-auto leading-relaxed"
          style={{ fontSize: "16px" }} // lg:text-[16px]
        >
          AI creates value only when it’s applied — to real challenges, with
          measurable results. We build bespoke AI solutions that power tangible
          transformation. From predictive analytics to learning ecosystems and
          intelligent automation, our platforms are redefining how businesses
          operate, decide, and grow.
        </p>
      </div>

      {/* Use Cases List */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        {useCases.map((useCase, index) => (
          <article key={useCase.id} className="mb-12 md:mb-16">
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg border border-gray-200">
              <div
                className={`flex flex-col ${
                  index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                } gap-6 md:gap-8`}
              >
                {/* Content Section */}
                <div className="w-full md:w-1/2">
                  {/* Title with Badge */}
                  <div className="mb-4">
                    <div className="bg-[#E8EAF6] text-[#3E59B7] px-4 py-2 rounded-lg inline-block mb-3">
                      <h2 className="font-bold text-[16px] md:text-[20px] text-left">
                        {useCase.title}
                      </h2>
                    </div>
                    <p className="font-medium mt-1 text-[12px] md:text-[14px] text-[#3E59B7]">
                      Powered by:{useCase.subtitle}
                    </p>
                  </div>

                  {/* Description */}
                  <p className="text-[#666666] mb-6 leading-relaxed text-[12px] md:text-[14px]">
                    {useCase.para}
                  </p>

                  {/* Challenge Section */}
                  <div className="bg-[#F5F5F5] px-4 py-2 rounded-lg mb-5">
                    <div className="mb-2">
                      <h3 className="font-bold text-[#3E59B7] text-[12px] md:text-[14px]">
                        Challenge
                      </h3>
                    </div>
                    <p className="text-[#666666] leading-relaxed px-2 text-[12px] md:text-[14px]">
                      {useCase.challenge}
                    </p>
                  </div>

                  {/* Solution Section */}
                  <div className="mb-5 bg-[#F5F5F5] px-4 py-2 rounded-lg">
                    <div className="mb-2">
                      <h3 className="font-bold text-[#3E59B7] text-[12px] md:text-[14px]">
                        Solution
                      </h3>
                    </div>
                    <p className="text-[#666666] leading-relaxed px-2 text-[12px] md:text-[14px]">
                      {useCase.solution}
                    </p>
                  </div>

                  {/* Results Section */}
                  <div className="mb-6 bg-[#F5F5F5] px-4 py-2 rounded-lg">
                    <div className="bg-[#F5F5F5] px-4 py-2 rounded-lg mb-2">
                      <h3 className="font-bold text-[#666666] text-[12px] md:text-[14px]">
                        Results
                      </h3>
                    </div>
                    <div className="px-2">
                      <p className="text-[#666666] leading-relaxed text-[12px] md:text-[14px]">
                        {useCase.results}
                      </p>
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 mt-6">
                    <button
                      onClick={handleOpenPopup}
                      className="bg-[#3e59b7] hover:bg-[#1976D2] text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 flex items-center justify-center"
                    >
                      Book a demo
                    </button>
                  </div>
                </div>

                {/* Image Section */}
                <div className="w-full md:w-1/2 flex items-center justify-center">
                  <div className="w-full h-64 md:h-full bg-gray-200 rounded-xl overflow-hidden">
                    <img
                      src={useCase.image}
                      alt={useCase.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </article>
        ))}
      </section>

      <section
        className="text-[#666666] min-h-[180px] md:min-h-[230px] relative mt-20 bg-no-repeat bg-cover"
        style={{
          background: "linear-gradient(100deg , #3E59B7 90%, #A6BAFF 110%)",
        }}
      >
        <figure className="absolute bottom-0 right-0">
          <img
            src={recright}
            alt="Additional"
            loading="lazy"
            className="lg:w-40 lg:h-auto w-24 h-36 md:w-40 md:h-full"
          />
        </figure>

        <article className="absolute inset-0 flex flex-col justify-center items-center text-center text-white pt-4 md:pt-10 md:mr-32 lg:pt-5 md:ml-20">
          <h2 className="font-bold mb-4 font-display text-[14px] md:text-[20px] lg:text-[24px]">
            Ready to Experience Our AI Platforms?
          </h2>

          <p className="font-light mb-4 font-display mx-2 text-[12px] md:text-[18px] lg:text-[18px]">
            Discover how our comprehensive AI platform suite can transform your{" "}
            <br />
            enterprise operations and drive unprecedented growth.
          </p>

          <div className="flex gap-4">
            <button
              className="md:mt-3 mb-3 rounded-xl border text-[#3E59B7] bg-white md:py-3 md:px-20 px-8 py-2 transition-all duration-300 ease-in-out hover:bg-transparent hover:border hover:border-white hover:text-[#FFFFFF]"
              onClick={handleOpenPopup}
            >
              <div className="flex items-center">
                <span className="font-bold text-[12px] md:text-[18px]">
                  Start Free Trial
                </span>
              </div>
            </button>

            <button
              className="md:mt-3 mb-3 rounded-xl border text-white border-white md:py-3 md:px-20 px-8 py-2 transition-all duration-300 ease-in-out bg-transparent hover:bg-white hover:text-[#3E59B7]"
              onClick={handleOpenPopup}
            >
              <div className="flex items-center">
                <span className="font-bold text-[12px] md:text-[18px]">
                  Schedule Demo
                </span>
              </div>
            </button>
          </div>
        </article>
      </section>

      {/* Call to Action Section */}

      {/* Popup Section */}
      {isPopupOpen && (
        <Popup
          utmContent={"use-cases"}
          isModalOpen={isPopupOpen}
          setIsModalOpen={setIsPopupOpen}
        />
      )}
    </section>
  );
};

export default UseCases;
